import { createFileRoute } from "@tanstack/react-router";
import type { BuilderLocationSettings, BuilderPricingVisibilitySettings } from "api-types/builders/builderTypes";
import type { BuilderSalesConfig } from "api-types/builders/salesConfig";
import type { BuilderAccount } from "api-types/common/responseTypes";
import type { ClientCredit } from "api-types/credits/creditsResponses";
import type { BuilderSalesConfig as ModelBuilderSalesConfig } from "api-types/modelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import BuilderAccountSalesConfig from "lib/components/account/BuilderAccountSalesConfig";
import BuilderInfoForm from "lib/components/builders/BuilderInfoForm";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import CopyBuilderData from "lib/components/builders/CopyBuilderData";
import CreditsReload from "lib/components/builders/credits/CreditsReload";
import CreditsTable from "lib/components/builders/credits/CreditsTable";
import LocationSettings from "lib/components/builders/LocationSettings";
import BrochureConfig from "lib/components/builders/marketing/BrochureConfig";
import PricingVisibilitySettings from "lib/components/builders/PricingVisibilitySettings";
import ProfileForm from "lib/components/builders/ProfileForm";
import { useFetch } from "lib/fetcher";
import type { AccountPageSection } from "lib/routeHelpers";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import withAuthentication from "../../../../components/withAuthentication";

function getCredits(credits: ClientCredit[]): number {
  return credits?.reduce((sum, c) => sum + c.amount, 0);
}

function AccountPage() {
  const { builderId } = useAppRouterParams();
  const { data, mutate: mutateBuilderAccount } = useFetch<BuilderAccount>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/account`,
    [builderId],
  );
  const { isSuperAdmin, hasPermission } = useUserContext();

  const visibleTabs: AccountPageSection[] = ["profile"];

  if (hasPermission("VIEW_BUILDER_INFO")) {
    visibleTabs.push("builder_info");
  }

  if (hasPermission("VIEW_BROCHURE_SETTINGS")) {
    visibleTabs.push("brochure");
  }
  if (hasPermission("VIEW_SALES_SETTINGS")) {
    visibleTabs.push("sales");
  }
  if (hasPermission("VIEW_CREDITS")) {
    visibleTabs.push("credits");
  }
  if (hasPermission("VIEW_PRICING_VISIBILITY")) {
    visibleTabs.push("pricing_visibility");
  }
  if (isSuperAdmin) {
    visibleTabs.push("copy_data");
  }
  if (hasPermission("VIEW_LOCATION_SETTINGS")) {
    visibleTabs.push("location_settings");
  }
  const { tab, setTab, menuItems } = useTabbedMenu<AccountPageSection>(visibleTabs);

  // Redirect if restricted tabs are typed directly into the URL hash
  if (
    (!hasPermission("VIEW_BUILDER_INFO") && tab === "builder_info") ||
    (!hasPermission("VIEW_BROCHURE_SETTINGS") && tab === "brochure") ||
    (!hasPermission("VIEW_CREDITS") && tab === "credits") ||
    (!hasPermission("VIEW_SALES_SETTINGS") && tab === "sales") ||
    (!hasPermission("VIEW_PRICING_VISIBILITY") && tab === "pricing_visibility") ||
    (!isSuperAdmin && tab === "copy_data") ||
    (!hasPermission("VIEW_LOCATION_SETTINGS") && tab === "location_settings")
  ) {
    setTab("profile");
  }

  return (
    <BuilderPortalLayout header="Account Settings" pageTitle="Account" selected="settings" menuItems={menuItems}>
      <div className="builder-portal-layout-content">
        {data && (
          <>
            {(() => {
              switch (tab) {
                case "profile":
                  return <ProfileForm />;
                case "builder_info":
                  if (!hasPermission("VIEW_BUILDER_INFO")) {
                    return null;
                  }
                  return <BuilderInfoForm />;
                case "brochure":
                  if (!hasPermission("VIEW_BROCHURE_SETTINGS")) {
                    return null;
                  }
                  return <BrochureConfig canEdit={hasPermission("EDIT_BROCHURE_SETTINGS")} />;
                case "credits":
                  if (!hasPermission("VIEW_CREDITS")) {
                    return null;
                  }
                  return (
                    <>
                      <SectionHeader title="Credits" subtitle={`Current balance: ${getCredits(data?.credits)}`} hr />
                      <CreditsReload
                        builder={data.builder}
                        lowCredits={getCredits(data?.credits) <= data?.builder?.creditReloadThreshold}
                        canEdit={hasPermission("EDIT_CREDITS")}
                      />
                      <CreditsTable credits={data.credits} />
                    </>
                  );
                case "sales":
                  if (!hasPermission("VIEW_SALES_SETTINGS")) {
                    return null;
                  }
                  return (
                    <>
                      <SectionHeader title="Sales" subtitle="Manage settings for Showroom" hr />
                      <BuilderAccountSalesConfig
                        builder={data.builder}
                        canEdit={hasPermission("EDIT_SALES_SETTINGS")}
                        mutateSalesConfig={(salesConfig: BuilderSalesConfig) =>
                          mutateBuilderAccount({
                            ...data,
                            builder: { ...data.builder, salesConfig: salesConfig as ModelBuilderSalesConfig },
                          })
                        }
                      />
                    </>
                  );
                case "pricing_visibility":
                  if (!hasPermission("VIEW_PRICING_VISIBILITY")) {
                    return null;
                  }
                  return (
                    <>
                      <SectionHeader
                        title="Pricing Visibility"
                        subtitle="Manage global settings for displaying prices"
                        hr
                      />
                      <PricingVisibilitySettings
                        builder={data.builder}
                        canEdit={hasPermission("EDIT_PRICING_VISIBILITY")}
                        mutatePricingVisibility={(pricingVisibility: BuilderPricingVisibilitySettings | null) =>
                          mutateBuilderAccount({
                            ...data,
                            builder: {
                              ...data.builder,
                              pricingVisibilitySettings: pricingVisibility as BuilderPricingVisibilitySettings,
                            },
                          })
                        }
                      />
                    </>
                  );
                case "copy_data":
                  if (!isSuperAdmin) {
                    return null;
                  }
                  return <CopyBuilderData builder={data.builder} />;
                case "location_settings":
                  if (!hasPermission("VIEW_LOCATION_SETTINGS")) {
                    return null;
                  }
                  return (
                    <LocationSettings
                      builder={data.builder}
                      canEdit={hasPermission("EDIT_LOCATION_SETTINGS")}
                      mutateLocationSettings={(locationSettings: BuilderLocationSettings | null) =>
                        mutateBuilderAccount({
                          ...data,
                          builder: {
                            ...data.builder,
                            locationSettings: locationSettings as BuilderLocationSettings,
                          },
                        })
                      }
                    />
                  );
              }
            })()}
          </>
        )}
      </div>
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/account/")({
  component: withAuthentication(AccountPage),
});
